// src/modules/app-views/crm/pages/Kanban/modals/TransferOpportunityModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfigProvider } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import crmService from '../../../../service';
import Step1 from './Step1';

// *******************************************************************************
// THEME WRAPPER
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN MODAL COMPONENT
// *******************************************************************************
const TransferOpportunityModal = ({ show, setShow, afterClose, card, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedUser = useZustand('auth', 'user');
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form }) => {
    // Validate required fields — AntD scrolls to first error + shows messages
    try {
      await form.validateFields(['boardUid', 'columnUid', 'consultantId']);
    } catch {
      return false;
    }

    const values = form.getFieldsValue(true);

    // 1. Move card to new board/column
    await crmService.moveOpportunity(card._id, {
      boardUid: values.boardUid,
      columnUid: values.columnUid,
    });

    // 2. Update consultant (both singular and array for pipeline __team population)
    await axiosService.put({
      url: API.CRM.URL.replace(':_id?', card._id),
      data: {
        consultantId: values.consultantId,
        consultantIds: [values.consultantId],
      },
      returnAsObject: true,
    });

    // 3. Log transfer in follow-up timeline
    const boards = storedParameters?.modules?.crm?.boards || [];
    const targetBoard = boards.find(b => b.uid === values.boardUid);
    const targetColumn = targetBoard?.columns?.find(c => c.uid === values.columnUid);
    await crmService.addMovementFollowUp(card._id, {
      user: storedUser,
      text: translateX('followup_transferred', {
        board: targetBoard?.title || '?',
        column: targetColumn?.title || '?',
      }),
    });

    // 4. Refresh kanban
    window.dispatchEvent(new CustomEvent('crm:kanban:refresh'));
    appService.message({ flag: 's', text: 'transferred_successfully' });
    onSuccess?.();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const currentBoardUid = card?.boardUid;

  const steps = useMemo(
    () => [
      {
        stepTitle: 'transfer_opportunity',
        content: () => (
          <Step1 currentBoardUid={currentBoardUid} />
        ),
      },
    ],
    [currentBoardUid]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      afterClose={afterClose}
      title={translateX('transfer_opportunity')}
      icon={<SwapOutlined />}
      steps={steps}
      onFinish={handleFinish}
      initialValues={{}}

      width={420}
    />
  );
};

// *******************************************************************************
// IMPERATIVE SHOW FUNCTION (default export)
// *******************************************************************************
function showTransferOpportunityModal({ card, onSuccess } = {}) {
  const container = document.createElement('div');
  container.id = 'transfer-opportunity-modal-container';
  document.body.appendChild(container);

  const root = createRoot(container);
  let isModalOpen = true;

  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <TransferOpportunityModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          card={card}
          onSuccess={onSuccess}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  renderModal();
}

export default showTransferOpportunityModal;
